import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Shield, UserPlus, Lock, Upload, FileDown, Building2, Users, Briefcase, CheckCircle2, AlertTriangle,
  Search, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import BudgetTargetsPanel from "@/components/BudgetTargetsPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import ZohoSignPanel from "@/components/ZohoSignPanel";
import MigrationPanel from "@/components/MigrationPanel";
import SalesTargetsAdminPanel from "@/components/SalesTargetsAdminPanel";
import LookupListsPanel from "@/components/LookupListsPanel";
import DedupePanel from "@/components/DedupePanel";
import TicketRecyclePanel from "@/components/TicketRecyclePanel";
import DangerZonePanel from "@/components/DangerZonePanel";

const ROLES = [
  { v: "admin", label: "Admin", desc: "Full access, manages users & catalog." },
  { v: "sales", label: "Sales", desc: "Accounts, deals, contacts, documents." },
  { v: "account_manager", label: "Account Manager", desc: "Accounts, renewals, support." },
  { v: "finance", label: "Finance", desc: "Billing, Xero, financial reports." },
  { v: "support", label: "Support", desc: "Tickets and product links." },
  { v: "leadership", label: "Leadership", desc: "Read-only portfolio dashboards." },
];

const ROLE_COLOR = {
  admin: "bg-blue-100 text-blue-800 border-blue-200",
  sales: "bg-emerald-100 text-emerald-800 border-emerald-200",
  account_manager: "bg-violet-100 text-violet-800 border-violet-200",
  finance: "bg-amber-100 text-amber-800 border-amber-200",
  support: "bg-orange-100 text-orange-800 border-orange-200",
  leadership: "bg-slate-100 text-slate-800 border-slate-200",
};

// ---------------------------------------------------------------------------
// Admin surface — 17 individual panels grouped into 5 workstreams so the top
// of the page isn't a 17-item tab bar that wraps and loses the engineer. The
// `tab` query param continues to point at a leaf panel key (e.g. `?tab=users`)
// so deep-links stay valid.
// ---------------------------------------------------------------------------
const ADMIN_SECTIONS = [
  {
    key: "people",
    label: "People & access",
    hint: "Teams, roles, reviews, merge/clean-up",
    items: [
      ["users",       "Users & roles",   "Create, deactivate and change role on CRM users"],
      ["people",      "People reviews",  "Quarterly PIP/1-to-1 and performance notes"],
      ["mergeusers",  "Merge users",     "Collapse duplicate user accounts into one"],
      ["reconcile",   "Owner reconcile", "Reassign records when someone leaves"],
    ],
  },
  {
    key: "sales",
    label: "Sales setup",
    hint: "Targets, pipeline knobs, dropdown vocabularies",
    items: [
      ["sales-targets", "Sales targets",   "Monthly MRR / revenue targets per rep"],
      ["budget",        "Budget targets",  "Yearly budget per cost centre"],
      ["lookup-lists",  "Dropdown lists",  "Industries, kill reasons, stages, etc."],
    ],
  },
  {
    key: "data",
    label: "Data operations",
    hint: "Imports, dedupe, geo + billing reconciliation",
    items: [
      ["import",     "Bulk import",       "CSV/Excel import for accounts, contacts, deals"],
      ["duplicates", "Duplicates",        "Find and merge duplicate accounts / contacts"],
      ["postcodes",  "Postcode map",      "Backfill lat/long so the geo map works"],
      ["chartmogul", "ChartMogul match",  "Link MRR customers to CRM accounts"],
      ["migration",  "Migration",         "Legacy data import & spreadsheet export"],
    ],
  },
  {
    key: "comms",
    label: "Comms & integrations",
    hint: "Email, chat, 3rd-party connectors",
    items: [
      ["notifications", "Notifications", "In-app and email alert routing"],
      ["integrations",  "Integrations",  "Microsoft 365, Anthropic, Xero, SendGrid, …"],
      ["zoho-sign",     "Zoho Sign",     "Upload a work order and send for e-signature"],
    ],
  },
  {
    key: "system",
    label: "System & safety",
    hint: "Security, bins, destructive admin",
    items: [
      ["security",       "Security",          "Login, lockout and session policy"],
      ["ticket-recycle", "Ticket recycle bin","Restore recently deleted tickets"],
      ["danger-zone",    "Danger zone",       "Purges and destructive one-time actions", true],
    ],
  },
];

// Flat lookup for the current panel key → human label (used in breadcrumbs)
const ADMIN_LABEL_BY_KEY = Object.fromEntries(
  ADMIN_SECTIONS.flatMap((s) => s.items.map(([k, l]) => [k, l])),
);
const ADMIN_SECTION_BY_KEY = Object.fromEntries(
  ADMIN_SECTIONS.flatMap((s) => s.items.map(([k]) => [k, s])),
);

export default function Admin() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "users");
  const [query, setQuery] = useState("");
  const [forbidden, setForbidden] = useState(false);

  // Probe access on mount
  useEffect(() => {
    api.get("/users").catch((e) => {
      if (e.response?.status === 403) setForbidden(true);
    });
  }, []);

  // Keep the current section expanded by default based on which tab the
  // engineer is on. Dangerzone is always its own card at the bottom.
  const activeSection = ADMIN_SECTION_BY_KEY[tab];

  // Live search across every leaf item — matches on label or hint. When
  // active, hides the section grouping and surfaces a flat result list so
  // "merge" or "chartmogul" lands you one click away from anywhere.
  const q = query.trim().toLowerCase();
  const flatSearch = q
    ? ADMIN_SECTIONS.flatMap((s) =>
        s.items
          .filter(([, label, hint]) =>
            (label || "").toLowerCase().includes(q) ||
            (hint  || "").toLowerCase().includes(q),
          )
          .map((item) => [s, item]),
      )
    : [];

  if (forbidden) {
    return (
      <Card className="p-12 text-center border-slate-200" data-testid="admin-forbidden">
        <Lock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <div className="font-heading text-lg text-slate-700">Admin access required</div>
        <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
          Only admins can manage users and bulk imports. Ask an existing admin to change your role from <span className="font-mono text-slate-700">{user?.role || "user"}</span>.
        </div>
      </Card>
    );
  }

  const renderPanel = () => {
    switch (tab) {
      case "users":          return <UsersPanel currentUser={user} />;
      case "import":         return <BulkImportPanel />;
      case "chartmogul":     return <ChartMogulMatchPanel />;
      case "people":         return <PeopleReviewsDashboard />;
      case "budget":         return <BudgetTargetsPanel />;
      case "sales-targets":  return <SalesTargetsAdminPanel />;
      case "lookup-lists":   return <LookupListsPanel />;
      case "duplicates":     return <DedupePanel />;
      case "postcodes":      return <PostcodeGeocodePanel />;
      case "reconcile":      return <OwnerReconcilePanel />;
      case "mergeusers":     return <MergeUsersPanel />;
      case "notifications":  return <NotificationsPanel />;
      case "integrations":   return <IntegrationsPanel />;
      case "zoho-sign":      return <ZohoSignPanel />;
      case "migration":      return <MigrationPanel />;
      case "ticket-recycle": return <TicketRecyclePanel />;
      case "security":       return <SecurityPanel />;
      case "danger-zone":    return <DangerZonePanel />;
      default:               return <UsersPanel currentUser={user} />;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Administration</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Admin</h1>
        <p className="text-slate-500 text-sm mt-1">
          Pick a workstream on the left; the panel on the right opens in place.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6">
        {/* ---------- Section sidebar ---------- */}
        <aside className="space-y-3" data-testid="admin-sidenav">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search admin…"
              className="h-9 pl-8 text-sm"
              data-testid="admin-search"
            />
          </div>
          {flatSearch.length > 0 ? (
            <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
              {flatSearch.map(([section, [k, label, hint]]) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => { setTab(k); setQuery(""); }}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3 ${tab === k ? "bg-blue-50/70" : ""}`}
                  data-testid={`admin-nav-${k}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900 truncate">{label}</div>
                    <div className="text-[11px] text-slate-500 truncate">{hint}</div>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 shrink-0">{section.label.split(" ")[0]}</span>
                </button>
              ))}
            </div>
          ) : (
            ADMIN_SECTIONS.map((section) => {
              const open = activeSection?.key === section.key;
              const isDanger = section.key === "system";
              return (
                <div
                  key={section.key}
                  className={`rounded-md border bg-white ${open ? "border-blue-200 shadow-sm" : "border-slate-200"}`}
                  data-testid={`admin-section-${section.key}`}
                >
                  <div className={`px-3 py-2 border-b ${open ? "border-blue-100 bg-blue-50/50" : "border-slate-100 bg-slate-50/70"}`}>
                    <div className={`text-[10px] font-mono uppercase tracking-wider ${open ? "text-blue-700" : "text-slate-500"}`}>
                      {section.label}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{section.hint}</div>
                  </div>
                  <div className="py-1">
                    {section.items.map(([k, label,, isDangerLeaf]) => {
                      const active = tab === k;
                      const danger = isDanger && isDangerLeaf;
                      return (
                        <button
                          type="button"
                          key={k}
                          onClick={() => setTab(k)}
                          data-testid={`admin-nav-${k}`}
                          className={[
                            "w-full text-left px-3 py-1.5 text-sm flex items-center gap-2",
                            active
                              ? (danger ? "bg-rose-50 text-rose-700 font-medium" : "bg-blue-50 text-blue-700 font-medium")
                              : (danger ? "text-rose-600 hover:bg-rose-50/60" : "text-slate-600 hover:bg-slate-50"),
                          ].join(" ")}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${active ? (danger ? "bg-rose-500" : "bg-blue-500") : "bg-slate-300"}`} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </aside>

        {/* ---------- Panel ---------- */}
        <section className="min-w-0">
          <div className="mb-4 flex items-center gap-2 text-xs text-slate-500 font-mono">
            <span className="text-slate-400">Admin</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-400">{activeSection?.label}</span>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-900">{ADMIN_LABEL_BY_KEY[tab]}</span>
          </div>
          <div data-testid={`admin-panel-${tab}`}>
            {renderPanel()}
          </div>
        </section>
      </div>
    </div>
  );
}

/* ---------------- Users panel ---------------- */
function UsersPanel({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "sales" });

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.email.trim() || !form.name.trim()) return toast.error("Email and name required");
    try {
      await api.post("/users", form);
      toast.success(`Added ${form.name}`);
      setOpen(false);
      setForm({ email: "", name: "", role: "sales" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add user");
    }
  };
  const updateRole = async (uid, role) => { await api.patch(`/users/${uid}`, { role }); load(); };
  const updateManager = async (uid, manager_user_id) => {
    await api.patch(`/users/${uid}`, { manager_user_id: manager_user_id === "__none__" ? null : manager_user_id });
    load();
  };
  const updateJobTitle = async (uid, job_title) => {
    await api.patch(`/users/${uid}`, { job_title: job_title || null });
    load();
  };
  const updateAliases = async (uid, csv) => {
    const list = csv.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    await api.patch(`/users/${uid}`, { additional_emails: list });
    load();
  };
  const remove = async (uid) => {
    if (!window.confirm("Delete this user? Their sessions will be revoked.")) return;
    try { await api.delete(`/users/${uid}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="gap-2 bg-blue-600 hover:bg-blue-700"><UserPlus className="h-4 w-4" /> New User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Full name *</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email * (Google sign-in)</Label><Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Role *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-[11px] text-slate-500 mt-1.5">{ROLES.find((r) => r.v === form.role)?.desc}</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="user-form-submit" onClick={create} className="bg-blue-600 hover:bg-blue-700">Add user</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Shield className="h-4 w-4 text-blue-600" /> Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No users yet.</div>
          ) : (
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Job title</th>
                  <th className="px-4 py-3">Additional emails</th>
                  <th className="px-4 py-3">Manager</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isMe = u.user_id === currentUser?.user_id;
                  return (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-slate-100">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{u.name}</div>
                        {isMe && <span className="text-[10px] font-mono uppercase tracking-wider text-blue-700">· you</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.email}</td>
                      <td className="px-4 py-3">
                        <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)}>
                          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-role-${u.user_id}`}>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${ROLE_COLOR[u.role] || ROLE_COLOR.sales}`}>
                              {ROLES.find((r) => r.v === u.role)?.label || u.role}
                            </span>
                          </SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          defaultValue={u.job_title || ""}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (u.job_title || "")) updateJobTitle(u.user_id, v);
                          }}
                          className="text-sm border border-transparent hover:border-slate-200 focus:border-blue-300 focus:outline-none rounded px-2 py-1 w-40 bg-transparent"
                          placeholder="—"
                          data-testid={`user-jobtitle-${u.user_id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          defaultValue={(u.additional_emails || []).join(", ")}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== (u.additional_emails || []).join(", ")) updateAliases(u.user_id, v);
                          }}
                          className="text-sm border border-transparent hover:border-slate-200 focus:border-blue-300 focus:outline-none rounded px-2 py-1 w-52 bg-transparent"
                          placeholder="alias@domain.com"
                          title="Extra mailboxes to merge into this person (e.g. a secondary domain). Comma-separated."
                          data-testid={`user-aliases-${u.user_id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <Select value={u.manager_user_id || "__none__"} onValueChange={(v) => updateManager(u.user_id, v)}>
                          <SelectTrigger className="h-8 w-44 text-xs" data-testid={`user-manager-${u.user_id}`}>
                            <SelectValue placeholder="No manager" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No manager</SelectItem>
                            {users.filter((mu) => mu.user_id !== u.user_id).map((mu) => (
                              <SelectItem key={mu.user_id} value={mu.user_id}>{mu.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {!isMe && (
                          <Button variant="ghost" size="icon" onClick={() => remove(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                            <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- Bulk import panel ---------------- */
function BulkImportPanel() {
  return (
    <div className="space-y-5">
      <Card className="border-slate-200 bg-blue-50/30">
        <CardContent className="p-5 flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-medium">How bulk import works</div>
            <div className="mt-1 text-slate-600 leading-relaxed">
              Download the template for each entity, fill it in, then upload. <span className="font-medium">Accounts</span> match by name (existing accounts will be <em>updated</em> rather than duplicated). <span className="font-medium">Contacts</span> and <span className="font-medium">deals</span> link to an account by <span className="font-mono">account_name</span> (or <span className="font-mono">account_id</span>) — make sure the account exists first.
            </div>
          </div>
        </CardContent>
      </Card>

      <ImportCard
        entity="accounts"
        title="Accounts"
        icon={Building2}
        accent="text-blue-600"
        fields="name (req), industry, website, status, health, lifecycle_stage, num_users, arr, mrr, owner_name, notes"
      />
      <ImportCard
        entity="contacts"
        title="Contacts"
        icon={Users}
        accent="text-emerald-600"
        fields="account_name (req), first_name (req), last_name (req), email, phone, job_title, is_primary, is_decision_maker, is_billing, is_support"
      />
      <ImportCard
        entity="deals"
        title="Deals"
        icon={Briefcase}
        accent="text-violet-600"
        fields="account_name (req), name (req), revenue, cost, stage, subscription_type, probability, close_date (YYYY-MM-DD), term_months, owner_name, notes"
      />
    </div>
  );
}

function ImportCard({ entity, title, icon: Icon, accent, fields }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const link = document.createElement("a");
    link.href = `${API}/admin/import/${entity}/template`;
    link.download = `${entity}_template.csv`;
    // For credentialed download, use fetch instead so cookie is sent
    fetch(link.href, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Download failed");
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${entity}_template.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Template download failed"));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/admin/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      const { created, updated, skipped } = r.data;
      toast.success(`${title}: ${created} created, ${updated} updated, ${skipped} skipped`);
    } catch (err) {
      toast.error("Import failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Card className="border-slate-200" data-testid={`import-card-${entity}`}>
      <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between gap-3">
        <div className="flex-1">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Icon className={`h-4 w-4 ${accent}`} /> Import {title}
          </CardTitle>
          <div className="text-xs text-slate-500 mt-1">Columns: <span className="font-mono">{fields}</span></div>
        </div>
        <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid={`download-template-${entity}`}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> Template
        </Button>
      </CardHeader>
      <CardContent className="p-5">
        <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-dashed border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors">
          <Upload className="h-4 w-4" />
          {uploading ? "Importing…" : "Upload CSV"}
          <input data-testid={`upload-${entity}`} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} disabled={uploading} />
        </label>

        {result && <ImportResult title={title} result={result} />}
      </CardContent>
    </Card>
  );
}

function ImportResult({ title, result }) {
  const ok = result.created + result.updated;
  const hasErrors = result.errors && result.errors.length > 0;
  return (
    <div className="mt-5 space-y-3" data-testid="import-result">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Rows" value={result.received} />
        <Stat label="Created" value={result.created} accent="text-emerald-700" />
        <Stat label="Updated" value={result.updated} accent="text-blue-700" />
        <Stat label="Skipped" value={result.skipped} accent={result.skipped ? "text-amber-700" : ""} />
      </div>

      {ok > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-100 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-emerald-900">
            Imported {ok} {title.toLowerCase()} successfully.
          </div>
        </div>
      )}

      {hasErrors && (
        <div className="rounded-md border border-amber-200 bg-amber-50/40 overflow-hidden">
          <div className="px-3 py-2 border-b border-amber-200 flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <span className="text-amber-900 font-medium">{result.errors.length} skipped row{result.errors.length > 1 ? "s" : ""}</span>
          </div>
          <div className="max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-amber-50">
                  <th className="px-3 py-1.5">Row</th>
                  <th className="px-3 py-1.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {result.errors.map((e, i) => (
                  <tr key={i} className="border-t border-amber-100">
                    <td className="px-3 py-1.5 font-mono text-slate-700">{e.row}</td>
                    <td className="px-3 py-1.5 text-slate-700">{e.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, accent = "" }) => (
  <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{label}</div>
    <div className={`font-heading text-xl font-bold mt-0.5 ${accent}`}>{value}</div>
  </div>
);


/* ---------------- ChartMogul Bulk Matcher ---------------- */
function ChartMogulMatchPanel() {
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [autoBind, setAutoBind] = React.useState(false);
  const [error, setError] = React.useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.post(`/chartmogul/bulk-match?auto_bind=${autoBind}`);
      setResult(r.data);
      if (autoBind) toast.success(`Bound ${r.data.bound_count} accounts to ChartMogul`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="chartmogul-bulk-panel">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Bulk-match accounts to ChartMogul</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Scans every unlinked account and matches it to a ChartMogul customer by company name. Enable auto-bind to commit exact matches in one click.
          </p>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoBind}
              onChange={(e) => setAutoBind(e.target.checked)}
              className="h-4 w-4"
              data-testid="bulk-auto-bind-checkbox"
            />
            <div>
              <div className="text-sm font-medium text-slate-900">Auto-bind exact matches</div>
              <div className="text-xs text-slate-500">Only single, unambiguous matches will be saved.</div>
            </div>
          </label>
          <button
            onClick={run}
            disabled={loading}
            data-testid="bulk-match-run-btn"
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? "Scanning ChartMogul…" : autoBind ? "Match & bind" : "Preview matches"}
          </button>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="CM customers" value={result.chartmogul_customers_indexed} />
            <Stat label="Accounts scanned" value={result.accounts_scanned} />
            <Stat label="Exact matches" value={result.exact_match_count} accent="text-emerald-700" />
            <Stat label="Multi-candidate" value={result.multiple_candidates_count} accent="text-amber-700" />
            <Stat label="Unmatched" value={result.unmatched_count} accent="text-slate-500" />
          </div>

          {result.multiple_candidates.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-heading">Need a human decision</CardTitle>
                <p className="text-xs text-slate-500 mt-1">More than one ChartMogul customer could match these accounts — open the account and pick one manually.</p>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-2">Account</th>
                      <th className="px-4 py-2">Candidates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.multiple_candidates.map((m) => (
                      <tr key={m.id} className="border-b border-slate-100">
                        <td className="px-4 py-2">
                          <a href={`/app/accounts/${m.id}`} className="text-blue-600 hover:underline font-medium">{m.name}</a>
                        </td>
                        <td className="px-4 py-2 text-[12px] text-slate-600">
                          {m.candidates.map((c) => `${c.company} (£${c.mrr})`).join(" · ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {result.unmatched.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-heading">Not in ChartMogul</CardTitle>
                <p className="text-xs text-slate-500 mt-1">No ChartMogul customer matched these account names. Probably prospects or non-subscription customers.</p>
              </CardHeader>
              <CardContent className="p-0 max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {result.unmatched.map((m) => (
                      <tr key={m.id} className="border-b border-slate-100">
                        <td className="px-4 py-2">
                          <a href={`/app/accounts/${m.id}`} className="text-blue-600 hover:underline">{m.name}</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}



/* ---------------- Security / IP allowlist panel ---------------- */
function SecurityPanel() {
  const [cfg, setCfg] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enforce, setEnforce] = useState(false);
  const [ipsText, setIpsText] = useState("");
  const [whoami, setWhoami] = useState(null);
  const [whoamiLoading, setWhoamiLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/security/waf/config"),
        api.get("/security/waf/recent?limit=50"),
      ]);
      setCfg(c.data);
      setRecent(r.data);
      setEnforce(c.data.enforce);
      setIpsText((c.data.allowed_ips || []).join("\n"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const checkMyIp = async () => {
    setWhoamiLoading(true);
    try {
      const r = await api.get("/security/whoami");
      setWhoami(r.data);
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setWhoamiLoading(false);
    }
  };

  const addIpsFromWhoami = (ip) => {
    const current = ipsText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (current.includes(ip)) {
      toast.info(`${ip} is already in the list`);
      return;
    }
    setIpsText([...current, ip].join("\n"));
    toast.success(`Added ${ip} — remember to click Save changes`);
  };

  const save = async () => {
    const ips = ipsText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      await api.patch("/security/waf/config", { enforce, allowed_ips: ips });
      toast.success(enforce ? `Enforcement ON — ${ips.length} IP(s) allowlisted` : "Saved (passive mode)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  }

  const allowed24h = cfg.last_24h?.allowed || 0;
  const blocked24h = cfg.last_24h?.blocked || 0;
  const ipsParsed = ipsText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);

  return (
    <div className="space-y-6" data-testid="security-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Security</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">IP allowlist</h2>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Restricts inbound traffic to a defined list of public IPs (your Barracuda WAF egress IPs). Any request arriving from an IP outside the list is rejected with HTTP 403.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm">Refresh</Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Shield className={`h-4 w-4 ${enforce ? "text-emerald-600" : "text-amber-600"}`} />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 grid lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-blue-900">
                  <strong>Important:</strong> the hosting platform replaces the client IP with internal infrastructure IPs. Click below to see what IPs your browser is actually arriving as — only those values will work in the allowlist.
                </div>
                <Button onClick={checkMyIp} variant="outline" size="sm" disabled={whoamiLoading} data-testid="waf-whoami-btn">
                  {whoamiLoading ? "Checking…" : "Show my IPs"}
                </Button>
              </div>
              {whoami && (
                <div className="mt-3 space-y-1">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-blue-900">IPs the app sees for you ({whoami.candidate_ips_seen_by_waf.length})</div>
                  {whoami.candidate_ips_seen_by_waf.length === 0 ? (
                    <div className="text-xs text-red-700">No client IPs are reaching the app at all. IP allowlisting is not viable on this hosting platform.</div>
                  ) : (
                    <ul className="space-y-1">
                      {whoami.candidate_ips_seen_by_waf.map((ip) => (
                        <li key={ip} className="flex items-center gap-2 font-mono text-xs">
                          <span className="text-slate-700 bg-white border border-blue-200 rounded px-2 py-0.5">{ip}</span>
                          <button
                            type="button"
                            onClick={() => addIpsFromWhoami(ip)}
                            className="text-[11px] text-blue-700 hover:text-blue-900 underline"
                          >
                            Add to allowlist
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Allowed public IPs / CIDR ranges</label>
              <textarea
                className="mt-1 w-full border border-slate-200 rounded font-mono text-sm p-3 h-44 focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder={"1.2.3.4\n5.6.7.0/24\n# one entry per line\n# IPs OR CIDR ranges accepted"}
                value={ipsText}
                onChange={(e) => setIpsText(e.target.value)}
                data-testid="waf-ips-textarea"
              />
              <div className="mt-1 text-xs text-slate-500">
                {ipsParsed.length} entr{ipsParsed.length === 1 ? "y" : "ies"} · accepts IPs or CIDR
              </div>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded p-3 bg-slate-50">
              <div>
                <div className="text-sm font-semibold text-slate-900">Enforce blocking</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {enforce
                    ? "Requests from non-allowlisted IPs return 403."
                    : "Passive: decisions are logged but nothing is blocked."}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={enforce}
                  onChange={(e) => setEnforce(e.target.checked)}
                  className="sr-only peer"
                  data-testid="waf-enforce-toggle"
                />
                <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
            {enforce && ipsParsed.length === 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                You can't enable enforcement with an empty list — it would lock everyone (including you) out.
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button
                onClick={save}
                disabled={saving || (enforce && ipsParsed.length === 0)}
                data-testid="waf-save-btn"
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
              {cfg.updated_at && (
                <span className="text-xs text-slate-500 font-mono">
                  last updated {new Date(cfg.updated_at).toLocaleString("en-GB")} by {cfg.updated_by || "—"}
                </span>
              )}
            </div>
          </div>

          <div className="space-y-3 lg:border-l lg:border-slate-100 lg:pl-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Mode</div>
              <div className="mt-1">
                {cfg.enforce ? (
                  <span className="px-2 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded" data-testid="waf-mode">ENFORCING</span>
                ) : (
                  <span className="px-2 py-1 text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 rounded" data-testid="waf-mode">PASSIVE — logs only</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Last 24h — total checks</div>
              <div className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="waf-total-24h">{(allowed24h + blocked24h).toLocaleString()}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-emerald-100 bg-emerald-50 rounded p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Allowed</div>
                <div className="font-heading text-2xl font-bold text-emerald-700 mt-1">{allowed24h.toLocaleString()}</div>
              </div>
              <div className="border border-red-100 bg-red-50 rounded p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-red-700">{cfg.enforce ? "Blocked" : "Would block"}</div>
                <div className="font-heading text-2xl font-bold text-red-700 mt-1">{blocked24h.toLocaleString()}</div>
              </div>
            </div>
            <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              Bypass paths: <span className="font-mono">{(cfg.bypass_paths || []).join(", ") || "—"}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Recent audit log</CardTitle>
          <p className="text-xs text-slate-500 mt-1">All blocked requests + a sample of allowed ones. 30-day retention.</p>
        </CardHeader>
        <CardContent className="p-0">
          {recent.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No entries yet — the IP check has not seen any traffic worth flagging.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Decision</th>
                    <th className="px-4 py-2">Method · Path</th>
                    <th className="px-4 py-2">Candidate IPs</th>
                    <th className="px-4 py-2">Matched</th>
                  </tr>
                </thead>
                <tbody data-testid="waf-audit-table">
                  {recent.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.ts).toLocaleString("en-GB")}</td>
                      <td className="px-4 py-2">
                        {r.allowed ? (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">ALLOW</span>
                        ) : (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-mono">BLOCK</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-700 font-mono truncate max-w-[280px]">{r.method} {r.path}</td>
                      <td className="px-4 py-2 text-xs text-slate-500 font-mono truncate max-w-[260px]">{(r.candidate_ips || []).join(", ")}</td>
                      <td className="px-4 py-2 text-xs text-slate-700 font-mono">{r.matched_ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ---------------- People reviews dashboard ---------------- */
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function PeopleReviewsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/people/admin/dashboard");
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-sm text-slate-400 font-mono">Loading…</div>;
  if (!data) return <div className="text-sm text-slate-500">No data.</div>;

  const cellColour = (status) => {
    if (status === "signed_off") return "bg-emerald-500 hover:bg-emerald-600";
    if (status === "submitted") return "bg-amber-400 hover:bg-amber-500";
    if (status === "draft") return "bg-slate-300 hover:bg-slate-400";
    return "bg-slate-100 hover:bg-slate-200";
  };

  return (
    <div className="space-y-5" data-testid="people-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Administration</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">People reviews</h2>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Each row is a person, each cell is a calendar month over the last 12. Hover for status; click to view a review.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Signed off</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400" /> Submitted</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-300" /> Draft</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-100 border border-slate-200" /> Missing</span>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="people-dashboard-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 sticky left-0 bg-slate-50 z-10">Person</th>
                <th className="px-3 py-3">Role</th>
                <th className="px-3 py-3 text-center">Completed (12mo)</th>
                <th className="px-3 py-3">Last signed off</th>
                {data.months.map((m) => (
                  <th key={`${m.year}-${m.month}`} className="px-2 py-3 text-center min-w-[50px]">
                    <div>{MONTH_ABBR[m.month - 1]}</div>
                    <div className="text-slate-400 font-normal">'{String(m.year).slice(-2)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`person-row-${row.user_id}`}>
                  <td className="px-4 py-2 sticky left-0 bg-white z-10">
                    <div className="font-medium text-slate-900 text-sm">{row.name}</div>
                    <div className="text-[11px] text-slate-500">{row.email}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{row.job_title || row.role}</td>
                  <td className="px-3 py-2 text-center font-mono text-sm">
                    <span className={`px-2 py-0.5 rounded border text-xs ${row.completed_count_12mo >= 9 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : row.completed_count_12mo >= 4 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                      {row.completed_count_12mo}/12
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">
                    {row.last_signed_off_at ? new Date(row.last_signed_off_at).toLocaleDateString("en-GB") : "—"}
                  </td>
                  {row.cells.map((c) => (
                    <td key={`${c.year}-${c.month}`} className="px-2 py-2 text-center">
                      <a
                        href={c.review_id ? `/app/people` : undefined}
                        className={`inline-block w-7 h-7 rounded-sm border ${cellColour(c.status)} ${c.status === "missing" ? "border-slate-200" : "border-transparent"} cursor-${c.review_id ? "pointer" : "default"}`}
                        title={`${MONTH_ABBR[c.month - 1]} ${c.year}: ${c.status}${c.signed_off_at ? " on " + new Date(c.signed_off_at).toLocaleDateString("en-GB") : ""}`}
                        aria-label={`${MONTH_ABBR[c.month - 1]} ${c.year} ${c.status}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


/* ---------------- Postcode geocode panel ---------------- */
function PostcodeGeocodePanel() {
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [mapStats, setMapStats] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/dashboard/map");
      setMapStats(r.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { refresh(); }, []);

  const run = async () => {
    setBusy(true);
    setSummary(null);
    try {
      const r = await api.post("/accounts/bulk-geocode");
      setSummary(r.data);
      toast.success(`Geocoded ${r.data.resolved || 0} accounts`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk geocode failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="postcode-geocode-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading">Postcode geocoding</CardTitle>
        <div className="text-xs text-slate-500 mt-1">
          Reads every account's postcode, resolves it to a lat/long via postcodes.io (free UK API, no key required)
          and caches the result. Used to plot the Customer Map on the dashboard.
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {mapStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">On map</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{mapStats.pins?.length || 0}</div>
            </div>
            <div className="p-3 rounded-md border border-amber-200 bg-amber-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Missing coords</div>
              <div className="font-heading text-2xl font-bold text-amber-900 mt-1">{mapStats.accounts_without_coords || 0}</div>
            </div>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            data-testid="run-bulk-geocode-btn"
            onClick={run}
            disabled={busy}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {busy ? "Geocoding…" : "Run bulk geocode"}
          </Button>
        </div>
        {summary && (
          <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="bulk-geocode-summary">
            Processed <span className="font-mono">{summary.processed}</span> · Resolved <span className="font-mono text-emerald-700">{summary.resolved}</span> · Failed <span className="font-mono text-rose-600">{summary.failed}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/* ---------------- Owner reconcile panel ---------------- */
function OwnerReconcilePanel() {
  const [url, setUrl] = useState("https://customer-assets-jt897jd0.emergentagent.net/job_xentra-stax/artifacts/jp68ef9i_live-accounts.xlsx");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (apply) => {
    if (!url.trim()) { toast.error("Paste the .xlsx URL first"); return; }
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/accounts/reconcile-owners-from-xlsx", {
        xlsx_url: url.trim(),
        apply,
        blank_owner_status: "prospect",
      });
      setResult(r.data);
      if (apply) toast.success(`Applied ${r.data.applied?.ok || 0} changes`);
      else toast.success("Dry run complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setBusy(false);
    }
  };

  const c = result?.counts;
  return (
    <Card className="border-slate-200" data-testid="owner-reconcile-panel">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading">Account owner reconciliation</CardTitle>
        <div className="text-xs text-slate-500 mt-1">
          Reads a workbook where col A is Account and col C is "Actual Owner". Present-owner rows re-assign the
          account owner; blank-owner rows flip the account status to <span className="font-mono">prospect</span>.
          Rows tagged <span className="font-mono">TEST</span> in col D are skipped.
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div>
          <Label className="text-xs">Workbook URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="owner-reconcile-url" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={() => run(false)} data-testid="owner-reconcile-dryrun-btn">
            {busy ? "Running…" : "Dry run"}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={() => run(true)} data-testid="owner-reconcile-apply-btn">
            {busy ? "Applying…" : "Apply changes"}
          </Button>
        </div>

        {c && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Reassign owner</div>
              <div className="font-heading text-2xl font-bold text-emerald-900 mt-1">{c.reassign_owner}</div>
            </div>
            <div className="p-3 rounded-md border border-blue-200 bg-blue-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-blue-700">Status → prospect</div>
              <div className="font-heading text-2xl font-bold text-blue-900 mt-1">{c.set_status}</div>
            </div>
            <div className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Already in sync</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{c.already_in_sync}</div>
            </div>
            <div className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Skipped TEST</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{c.skipped_test}</div>
            </div>
            <div className="p-3 rounded-md border border-amber-200 bg-amber-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Missing accounts</div>
              <div className="font-heading text-2xl font-bold text-amber-900 mt-1">{c.missing_accounts}</div>
            </div>
            <div className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Unknown owner user</div>
              <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{c.unknown_owner_users}</div>
            </div>
          </div>
        )}

        {result?.reassign?.length > 0 && (
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Reassignments ({result.reassign.length})</div>
            <div className="max-h-56 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {result.reassign.map((r) => (
                <div key={r.id} className="px-3 py-1.5 text-xs flex items-center gap-2">
                  <span className="flex-1 font-medium text-slate-800">{r.account}</span>
                  <span className="text-slate-500">{r.from}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-medium text-slate-800">{r.to}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {result?.set_status?.length > 0 && (
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">Status → prospect ({result.set_status.length})</div>
            <div className="max-h-56 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {result.set_status.map((r) => (
                <div key={r.id} className="px-3 py-1.5 text-xs flex items-center gap-2">
                  <span className="flex-1 font-medium text-slate-800">{r.account}</span>
                  <span className="text-slate-500">owner: {r.keep_owner}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {result?.missing?.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900">
            <div className="font-semibold mb-1">{result.missing.length} account(s) from the sheet not found in the portal:</div>
            <ul className="list-disc pl-5">
              {result.missing.map((m) => <li key={m.row}>{m.account}</li>)}
            </ul>
          </div>
        )}

        {result?.applied && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            Applied — <span className="font-mono text-emerald-700">{result.applied.ok} ok</span>, <span className="font-mono text-rose-600">{result.applied.failed} failed</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}



/* ---------------- Merge users panel ---------------- */
function MergeUsersPanel() {
  const [dupes, setDupes] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, u, a] = await Promise.all([
      api.get("/admin/users/duplicates"),
      api.get("/users"),
      api.get("/admin/users/merge-audit"),
    ]);
    setDupes(d.data || []);
    setAllUsers(u.data || []);
    setAudit(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const preview = async () => {
    if (!fromId || !toId) return toast.error("Pick both users");
    setBusy(true); setPlan(null);
    try {
      const r = await api.post("/admin/users/merge", { from_user_id: fromId, to_user_id: toId, apply: false });
      setPlan(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (!plan) return toast.error("Preview first");
    if (!window.confirm(`Merge ${plan.from.email} → ${plan.to.email}?\nThis will repoint ${plan.total_refs} reference(s) and delete the "from" user.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/users/merge", { from_user_id: fromId, to_user_id: toId, apply: true });
      toast.success(`Merged — repointed ${r.data.total_refs} references`);
      setPlan(null); setFromId(""); setToId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
    finally { setBusy(false); }
  };
  const pickPair = (fromU, toU) => { setFromId(fromU.user_id); setToId(toU.user_id); setPlan(null); };

  return (
    <div className="space-y-4" data-testid="merge-users-panel">
      {/* Duplicates */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Duplicate staff records</CardTitle>
          <div className="text-xs text-slate-500 mt-1">Users sharing a name. Click <span className="font-mono">Use</span> to preload the merge form.</div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          {dupes.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">No duplicates 🎉</div>
          ) : dupes.map((d) => (
            <div key={d.name} className="border border-slate-200 rounded-md p-3 bg-amber-50/40" data-testid={`dupe-group-${d.name.replace(/\s+/g,'-').toLowerCase()}`}>
              <div className="text-sm font-semibold text-slate-900 mb-2">{d.name} <span className="text-xs text-slate-500 ml-1">({d.users.length} records)</span></div>
              <div className="space-y-1.5">
                {d.users.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-slate-500 flex-shrink-0">{u.user_id}</span>
                    <span className="text-slate-700 flex-1 truncate">{u.email}</span>
                    <span className="text-slate-400">created {u.created_at?.slice(0, 10)}</span>
                    <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                    <Button size="sm" variant="outline" className="ml-1" onClick={() => setFromId(u.user_id)} data-testid={`set-from-${u.user_id}`}>Merge from</Button>
                    <Button size="sm" variant="outline" onClick={() => setToId(u.user_id)} data-testid={`set-to-${u.user_id}`}>Merge into</Button>
                  </div>
                ))}
                {d.users.length === 2 && (
                  <div className="pt-1">
                    <Button size="sm" onClick={() => pickPair(d.users[0], d.users[1])} className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`quick-pick-${d.name.replace(/\s+/g,'-').toLowerCase()}`}>
                      Use oldest → newest
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Manual merge */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Merge users</CardTitle>
          <div className="text-xs text-slate-500 mt-1">Repoints every reference (accounts, leads, deals, projects, tickets, sessions, reviews, direct-reports) then deletes the "from" user. Full audit trail kept.</div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Merge from (this user disappears)</Label>
              <Select value={fromId} onValueChange={(v) => { setFromId(v); setPlan(null); }}>
                <SelectTrigger data-testid="merge-from-select"><SelectValue placeholder="Pick user…" /></SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Merge into (this user stays)</Label>
              <Select value={toId} onValueChange={(v) => { setToId(v); setPlan(null); }}>
                <SelectTrigger data-testid="merge-to-select"><SelectValue placeholder="Pick user…" /></SelectTrigger>
                <SelectContent>
                  {allUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} — {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={preview} disabled={busy || !fromId || !toId} data-testid="merge-preview-btn">
              {busy ? "…" : "Preview merge"}
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={apply} disabled={busy || !plan || plan.applied} data-testid="merge-apply-btn">
              {busy ? "Merging…" : "Apply merge"}
            </Button>
          </div>

          {plan && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-2" data-testid="merge-plan">
              <div><b>From</b> <span className="font-mono">{plan.from.email}</span> → <b>To</b> <span className="font-mono">{plan.to.email}</span></div>
              <div className="font-mono">{plan.total_refs} reference(s) to move:</div>
              <ul className="pl-5 list-disc">
                {plan.impact.map((r) => <li key={`${r.collection}.${r.field}`}><span className="font-mono">{r.collection}.{r.field}</span> → {r.count}</li>)}
                {plan.impact.length === 0 && <li className="text-slate-500">No references — user has no data attached.</li>}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Merge audit */}
      {audit.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Merge history</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">From</th>
                  <th className="px-3 py-2">Into</th>
                  <th className="px-3 py-2 text-right">Refs moved</th>
                  <th className="px-3 py-2">By</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{(a.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-xs"><div>{a.from_name}</div><div className="text-slate-500 font-mono">{a.from_email}</div></td>
                    <td className="px-3 py-2 text-xs"><div>{a.to_name}</div><div className="text-slate-500 font-mono">{a.to_email}</div></td>
                    <td className="px-3 py-2 text-right font-mono">{(a.impact || []).reduce((s, x) => s + x.count, 0)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{a.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

